import { createParser, type EventSourceMessage } from 'eventsource-parser';
import type { RefObject } from 'react';
import type {
  CompletionExtractionResult,
  SummaryCompletionPayload,
  SummaryStateUpdate,
  SummaryStatusPayload,
  SummaryTokenPayload,
  TokenSelectionContext,
} from '@/components/shared/link-preview/summary/types';
import type { SummaryMetadata, SummaryState, SummaryStateSetter } from '@/components/shared/link-preview/types';
import { isRecord } from '@/lib/utils/type-guards';

const isStructuredPayload = (value: unknown): value is Record<string, unknown> => isRecord(value);

const isSummaryStatusPayload = (value: unknown): value is SummaryStatusPayload => isStructuredPayload(value);
const isSummaryTokenPayload = (value: unknown): value is SummaryTokenPayload => isStructuredPayload(value);
const isSummaryCompletionPayload = (value: unknown): value is SummaryCompletionPayload => isStructuredPayload(value);

const readErrorMessage = (record: Record<string, unknown>): string | null => {
  const candidate = (record as { message?: unknown }).message;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
};

export interface SummarizerStreamHandlers {
  onCompletion?: (
    payload: SummaryCompletionPayload,
    metadata: SummaryMetadata,
    aggregated: string
  ) => CompletionExtractionResult;
  onErrorMessage?: (payload: Record<string, unknown>) => string | null;
  onStatus?: (payload: SummaryStatusPayload, metadata: SummaryMetadata) => SummaryMetadata;
  onToken?: (
    payload: SummaryTokenPayload,
    metadata: SummaryMetadata,
    context: TokenSelectionContext
  ) => CompletionExtractionResult;
}

export interface ConsumeSummarizerStreamArguments {
  controller: AbortController;
  defaultErrorMessage: string;
  handlers: SummarizerStreamHandlers;
  initialAggregatedText?: string;
  initialMetadata: SummaryMetadata;
  isMountedRef: RefObject<boolean>;
  setSummaryState: SummaryStateSetter;
  stream: ReadableStream<Uint8Array>;
}

// Shared SSE consumer that keeps the link + video summarizer flows in sync while streaming Gemini/OpenAI tokens.
export async function consumeSummarizerStream({
  stream,
  controller,
  isMountedRef,
  setSummaryState,
  initialMetadata,
  defaultErrorMessage,
  handlers,
  initialAggregatedText = '',
}: ConsumeSummarizerStreamArguments): Promise<void> {
  let metadata: SummaryMetadata = { ...initialMetadata };
  let aggregated = initialAggregatedText;
  let streamErrored = false;

  const publishState = ({ status, text, error }: SummaryStateUpdate) => {
    if (!isMountedRef.current) return;
    setSummaryState((previous: SummaryState) => ({
      status: status ?? previous.status,
      text: text ?? previous.text,
      error: error === undefined ? previous.error : error,
      metadata: { ...metadata },
    }));
  };

  const recordStreamError = (message: string) => {
    streamErrored = true;
    publishState({ status: 'error', text: aggregated, error: message });
    if (!controller.signal.aborted) {
      controller.abort();
    }
  };

  const reader = stream.getReader();
  const decoder = new TextDecoder();

  const applyStatus = handlers.onStatus;
  const applyToken = handlers.onToken;
  const applyCompletion = handlers.onCompletion;
  const resolveErrorMessage = handlers.onErrorMessage;

  const processEvent = (event: EventSourceMessage) => {
    if (!event.data) return;
    try {
      const payload = JSON.parse(event.data) as unknown;
      switch (event.event) {
        case 'status': {
          if (applyStatus && isSummaryStatusPayload(payload)) {
            metadata = applyStatus(payload, metadata);
          }
          publishState({});
          break;
        }
        case 'token': {
          if (applyToken && isSummaryTokenPayload(payload)) {
            const result = applyToken(payload, metadata, { previousText: aggregated });
            aggregated = result.text;
            metadata = result.metadata;
          }
          publishState({ status: 'streaming', text: aggregated, error: null });
          break;
        }
        case 'done': {
          if (applyCompletion && isSummaryCompletionPayload(payload)) {
            const result = applyCompletion(payload, metadata, aggregated);
            aggregated = result.text;
            metadata = result.metadata;
          }
          publishState({ status: 'complete', text: aggregated, error: null });
          break;
        }
        case 'error': {
          const errorPayload = isStructuredPayload(payload) ? payload : {};
          const errorMessage =
            resolveErrorMessage?.(errorPayload) ?? readErrorMessage(errorPayload) ?? defaultErrorMessage;
          recordStreamError(errorMessage);
          break;
        }
        case undefined: {
          break;
        }
        default: {
          break;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : defaultErrorMessage;
      recordStreamError(message);
    }
  };

  const parser = createParser({ onEvent: processEvent });
  const pump = async (): Promise<void> => {
    const { done, value } = await reader.read();
    if (done) return;
    parser.feed(decoder.decode(value, { stream: true }));
    await pump();
  };

  await pump();

  if (!streamErrored && aggregated.length === 0) {
    publishState({ status: 'complete', text: '', error: null });
  }
}
